import React, { useEffect, useState } from "react";
import { colors } from "../../../theme/colors";
import {
  Backdrop,
  CircularProgress,
  Typography,
  Box,
  Button,
  Chip,
  CardContent,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import DownloadIcon from "@mui/icons-material/Download";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import GlobalTalentSearch from "./GlobalTalentSearch";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import VisibilityIcon from "@mui/icons-material/Visibility";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CandidateDetailsModal from "./CandidateDetailsModal";
import CommonSnackbar from "../../../components/common-components/CommonSnackbar";

import {
  GlobalTalentContainer,
  ResultsSection,
  ResultCard,
  CandidateHeader,
  CandidateInfo,
  CandidateDetails,
  SkillsSection,
  ExperienceSection,
  ActionSection,
  StyledDownloadButton,
  PaginationContainer,
  NoResultsContainer,
  StatsCard,
  ReadMoreButton,
  GradientOverlay,
  PersonalInfoContainer,
} from "./style";
import GlobalTalentSearchQuery from "./GlobalTalentSearchQuery";
import useGlobalSearch from "./useGlobalSearch";
import { CustomStyledCircularProgress } from "../../../components/common-components/CustomStyledCircularProgress";

const GlobalSearch = () => {
  const [selectedModalCandidate, setSelectedModalCandidate] = useState(null);

  const {
    data,
    scrollRef,
    selectedValues,
    paginatedData,
    formik,
    headCells,
    backDrop,
    filterOptions,
    disable,
    query,
    isGlobalSearching,
    isGlobalSearchingQuery,
    inputRef,
    locationInputs,
    skillInput,
    experienceMarks,
    locationDropdownOpen,
    setLocationDropdownOpen,
    selectedCountry,
    countryOptions,
    citySearchQuery,
    setCitySearchQuery,
    loadingCities,
    filteredCities,
    handleCountrySelect,
    handleCitySelect,
    handleLocationDropdownClose,
    handleLocationRemove,
    handleLocationClear,
    accordionExpanded,
    cardHeights,
    expandedCards,
    searchSuggestions,
    expandedSkills,
    downloadingResume,
    snackbar,
    setSnackbar,

    setSelectedValues,
    setQuery,
    setDisabled,
    handlePrevPage,
    handleNextPage,
    handleChange,
    handleDownloadResume,
    handleSearchGlobalTalentQuery,
    handleSearchGlobalTalent,
    handleKeyPress,
    setSkillInput,
    setAccordionExpanded,
    handleSuggestionClick,
    toggleSkillsExpansion,
    toggleCardExpansion,
    checkCardHeight,
    handleResetSearch,
    handleClearSearchResults,
  } = useGlobalSearch();

  const renderCandidateCard = (candidate, index) => {
    const candidateId = candidate?.id || index;
    const skills = candidate.metadata.skills || [];
    const displayedSkills = skills.slice(0, 4);
    const hasMoreSkills = skills.length > 4;

    return (
      <ResultCard key={candidateId}>
        <CardContent
          sx={{
            p: "18px !important",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
          }}
        >
          <CandidateHeader>
            <Avatar
              className="avatar"
              src={candidate.metadata.profilePic}
              alt={candidate.metadata.name}
            />
            <CandidateInfo>
              <Typography variant="h6" className="candidate-name">
                {candidate.metadata.name}
              </Typography>
              <Typography variant="body2" className="candidate-title">
                {candidate.metadata.title}
              </Typography>
            </CandidateInfo>
            <Box className="match-score">
              <Box className="score-badge">
                {candidate.final_score}%
              </Box>
              <Typography variant="caption" className="score-label">
                Match Score
              </Typography>

              <Box sx={{ mt: 0.5 }}>
                <Tooltip title="Download Resume" arrow>
                  <IconButton
                    className="download-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadResume(
                        candidate.id,
                        candidate.metadata.name,
                        `card_icon_${candidateId}`
                      );
                    }}
                    disabled={downloadingResume[`card_icon_${candidateId}`]}
                  >
                    {downloadingResume[`card_icon_${candidateId}`] ? (
                      <CustomStyledCircularProgress
                        size={18}
                        sx={{ color: colors.primary }}
                      />
                    ) : (
                      <SaveAltIcon size={18} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CandidateHeader>

          <PersonalInfoContainer>
            <Box className="candidate-location">
              <LocationOnIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {candidate.metadata.location?.slice(0, 2).join(", ")}
              </Typography>
            </Box>

            {candidate.metadata.email && (
              <Box display="flex" alignItems="start" gap={0.5}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {candidate.metadata.email}
                </Typography>
              </Box>
            )}

            {candidate.metadata.phone_number && (
              <Box display="flex" alignItems="start" gap={0.5}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {candidate.metadata.phone_number}
                </Typography>
              </Box>
            )}
          </PersonalInfoContainer>

          <Divider sx={{ my: 1.25 }} />

          <CandidateDetails>
            <Box className="detail-item">
              <WorkIcon fontSize="small" />
              <Typography variant="body2">
                {candidate.metadata.years_of_experience} years experience
              </Typography>
            </Box>
            <Box className="detail-item">
              <SchoolIcon fontSize="small" />
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {candidate.metadata.education?.join(", ")}
              </Typography>
            </Box>
          </CandidateDetails>

          <SkillsSection sx={{ mt: 1 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Top Skills
            </Typography>

            <Box className="skills-container">
              {displayedSkills.map((skill, idx) => (
                <Chip
                  key={idx}
                  label={skill}
                  size="small"
                  className="skill-chip"
                  variant="outlined"
                />
              ))}
              {hasMoreSkills && (
                <Chip
                  label={`+${skills.length - 4} more`}
                  size="small"
                  variant="filled"
                  clickable
                  className="has-more-skills"
                  onClick={() => setSelectedModalCandidate(candidate)}
                />
              )}
            </Box>
          </SkillsSection>

          {candidate.metadata.experience && candidate.metadata.experience.length > 0 && (
            <ExperienceSection sx={{ mt: 1 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                Recent Experience
              </Typography>

              {candidate.metadata.experience.slice(0, 1).map((exp, idx) => (
                <Typography
                  key={idx}
                  variant="body2"
                  className="experience-item"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  • {exp}
                </Typography>
              ))}
            </ExperienceSection>
          )}

          <Box sx={{ display: "flex", gap: 1.5, mt: "auto", pt: 2 }}>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<VisibilityIcon />}
              onClick={() => setSelectedModalCandidate(candidate)}
              sx={{
                flex: 1,
                borderColor: colors.primaryLightBorder,
                color: colors.primary,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "10px",
                py: 0.8,
                "&:hover": {
                  borderColor: colors.primary,
                  backgroundColor: colors.primaryLight,
                },
              }}
            >
              Read More
            </Button>

            <StyledDownloadButton
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadResume(
                  candidate.id,
                  candidate.metadata.name,
                  `card_btn_${candidateId}`
                );
              }}
              disabled={downloadingResume[`card_btn_${candidateId}`]}
              sx={{ flex: 1, py: 0.8 }}
            >
              {downloadingResume[`card_btn_${candidateId}`]
                ? "Downloading..."
                : "Resume"}
            </StyledDownloadButton>
          </Box>
        </CardContent>
      </ResultCard>
    );
  };

  return (
    <GlobalTalentContainer>
      <CommonSnackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        setOpen={setSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={backDrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <GlobalTalentSearchQuery
        handleSearchGlobalTalentQuery={handleSearchGlobalTalentQuery}
        query={query}
        setQuery={setQuery}
        isGlobalSearchingQuery={isGlobalSearchingQuery}
        inputRef={inputRef}
        handleKeyPress={handleKeyPress}
        accordionExpanded={accordionExpanded}
        setAccordionExpanded={setAccordionExpanded}
        searchSuggestions={searchSuggestions}
        handleSuggestionClick={handleSuggestionClick}
      />

      <GlobalTalentSearch
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        disable={disable}
        setDisabled={setDisabled}
        handleSearchGlobalTalent={handleSearchGlobalTalent}
        handleChange={handleChange}
        headCells={headCells}
        formik={formik}
        isGlobalSearching={isGlobalSearching}
        handleKeyPress={handleKeyPress}
        locationInput={locationInputs}
        skillInput={skillInput}
        setSkillInput={setSkillInput}
        experienceMarks={experienceMarks}
        locationDropdownOpen={locationDropdownOpen}
        setLocationDropdownOpen={setLocationDropdownOpen}
        selectedCountry={selectedCountry}
        countryOptions={countryOptions}
        citySearchQuery={citySearchQuery}
        setCitySearchQuery={setCitySearchQuery}
        loadingCities={loadingCities}
        filteredCities={filteredCities}
        handleCountrySelect={handleCountrySelect}
        handleCitySelect={handleCitySelect}
        handleLocationDropdownClose={handleLocationDropdownClose}
        handleLocationRemove={handleLocationRemove}
        handleLocationClear={handleLocationClear}
        handleResetSearch={handleResetSearch}
      />

      {data.results && data.results.length > 0 && (
        <ResultsSection ref={scrollRef}>
          <StatsCard>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                width: "100%",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" className="stats-title">
                  Search Results
                </Typography>
                <Typography variant="body1" className="stats-count">
                  Found {data.results.length} candidates matching your criteria
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearSearchResults}
                startIcon={<RestartAltIcon />}
                sx={{
                  color: colors.primary,
                  borderColor: colors.primaryLightBorder,
                  backgroundColor: colors.primaryLight,
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 2,
                  py: 0.8,
                  "&:hover": {
                    backgroundColor: colors.sidebarSelected,
                    borderColor: colors.primary,
                  },
                }}
              >
                Clear Search Results
              </Button>
            </Box>
          </StatsCard>

          <Box className="results-grid">
            {paginatedData.map((candidate, index) =>
              renderCandidateCard(candidate, index)
            )}
          </Box>
          <PaginationContainer>
            <Button
              className="btn"
              variant="contained"
              onClick={handlePrevPage}
              disabled={filterOptions.page <= 1}
            >
              Previous
            </Button>
            <Typography variant="body1" className="page-info">
              Page {filterOptions.page} of{" "}
              {Math.ceil(data.results?.length / filterOptions.limit)}
            </Typography>
            <Button
              className="btn"
              variant="contained"
              onClick={handleNextPage}
              disabled={
                filterOptions.page >=
                Math.ceil(data.results?.length / filterOptions.limit)
              }
            >
              Next
            </Button>
          </PaginationContainer>
        </ResultsSection>
      )}

      {data.results && data.results.length === 0 && (
        <NoResultsContainer ref={scrollRef}>
          <NoAccountsIcon className="no-results-icon" />
          <Typography variant="h5" className="no-results-title">
            No candidates found
          </Typography>
          <Typography variant="body1" className="no-results-subtitle">
            Try adjusting your search criteria or using different keywords
          </Typography>
        </NoResultsContainer>
      )}

      <CandidateDetailsModal
        open={!!selectedModalCandidate}
        onClose={() => setSelectedModalCandidate(null)}
        candidate={selectedModalCandidate}
        handleDownloadResume={handleDownloadResume}
        isDownloading={
          selectedModalCandidate
            ? downloadingResume[`modal_${selectedModalCandidate.id}`]
            : false
        }
      />
    </GlobalTalentContainer>
  );
};

export default GlobalSearch;
